import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'
import styled, { css } from 'styled-components'

import { Array, AsyncStateN } from '@blade/std-lib'
import { AttentionIcon, CircularLoader } from '@blade/ui-kit'
import {
  refreshWalletsAction,
  startNewWalletFlowEvent,
  walletsAtom,
  walletsStateAtom,
} from '@root/entities/wallet/atoms'
import { tradingWalletAtom } from '@root/features/trade/trading'
import { useTranslation } from '@root/shared/localization'
import { FadeOverflow, InfoMessage, WalletIcon } from '@root/shared/ui-kit'

import { InstantTradeWallet } from './instant-trade-wallet'
import { InstantTradeWalletsButton } from './instant-trade-wallets-button'

import type { Pair } from '@root/entities/pair'

export const InstantTradeWallets: React.FC<{ pair: Pair.PairMinimal }> = ({
  pair,
}) => {
  const { t } = useTranslation()
  const [wallets] = useAtom(walletsAtom)
  const [tradingWallet] = useAtom(tradingWalletAtom)
  const [walletsState] = useAtom(walletsStateAtom)
  const refreshWallets = useAction(refreshWalletsAction)
  const startWalletFlow = useAction(startNewWalletFlowEvent)

  return AsyncStateN.matchWaiting(walletsState, {
    Success: (walletsData) => {
      const isEmpty = Array.isEmpty(walletsData)

      if (isEmpty) {
        return (
          <InfoMessage
            intent="empty"
            variant="dt"
            icon={<WalletIcon />}
            title={t('wallet.instant_dialog.no_wallets')}
            description={t('wallet.instant_dialog.open_first')}
            css={css`
              margin-top: 12px;
            `}
            withSeparator
          >
            <InstantTradeWalletsButton onCreateWallet={startWalletFlow} />
          </InfoMessage>
        )
      }

      return (
        <Root>
          <List mode="always" variant="vertical">
            {wallets.map((wallet) => (
              <InstantTradeWallet
                key={wallet.id}
                wallet={wallet}
                pair={pair}
                selected={tradingWallet?.id === wallet.id}
              />
            ))}
          </List>
        </Root>
      )
    },
    Error: () => (
      <InfoMessage
        intent="error"
        variant="dt"
        icon={<AttentionIcon />}
        title={t('wallet.list-wallets.error.title')}
        description={t('wallet.list-wallets.error.description')}
        withSeparator
        onRetry={() => refreshWallets()}
      />
    ),
    Waiting: () => (
      <WalletsLoading>
        <CircularLoader size={32} />
      </WalletsLoading>
    ),
  })
}

const Root = styled.div`
  display: grid;

  height: 100%;
  min-height: 0;
`

const List = styled(FadeOverflow)`
  display: grid;

  gap: 6px;
  padding: var(--instant-trade-wallets-padding, 12px);

  max-height: 100%;
  overflow-y: auto;
  align-content: start;
`

const WalletsLoading = styled.div`
  display: grid;
  place-items: center;
`
